import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import useSWR from "swr";

/**
 * 상품명 자동완성 — 입력을 디바운스해 /manager/change-request/product-search 조회.
 * - value/onChange: 상품명 텍스트 제어
 * - onSelect(item): 추천 선택 시 호출 (item: {id, category, brand_id, brand_eng, brand_kr, product_name})
 *   → 폼에서 카테고리/브랜드 동반 채움
 */
function ProductAutocomplete({ value, onChange, onSelect, placeholder }) {
  const [term, setTerm] = useState(""); // 디바운스된 검색어
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  const { data } = useSWR(
    term.trim().length >= 1
      ? `/manager/change-request/product-search?search=${encodeURIComponent(
          term.trim()
        )}`
      : null
  );
  const list = Array.isArray(data?.products) ? data.products : [];

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const onType = (e) => {
    const v = e.target.value;
    onChange(v);
    setOpen(true);
    setActive(0);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setTerm(v), 400);
  };

  const choose = (item) => {
    onSelect(item);
    setTerm("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || list.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, list.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (list[active]) choose(list[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <Wrap ref={wrapRef}>
      <Field
        value={value}
        placeholder={placeholder}
        onFocus={() => value && setOpen(true)}
        onChange={onType}
        onKeyDown={onKeyDown}
      />
      {open && list.length > 0 && (
        <List>
          {list.map((it, i) => (
            <ItemBtn
              key={it.id}
              type="button"
              active={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(it);
              }}
            >
              <Cat>{it.category}</Cat>
              <Nm>
                {it.brand_eng ? `${it.brand_eng} ` : ""}
                {it.product_name}
              </Nm>
            </ItemBtn>
          ))}
        </List>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;
const Field = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 14px;
  font-size: 15px;
  border: 1px solid #d9dde2;
  border-radius: 10px;
  background: #ffffff;
  color: #222222;
  outline: none;
  &:focus {
    border-color: #003580;
  }
  &::placeholder {
    color: #b8bcc2;
  }
`;
const List = styled.div`
  position: absolute;
  z-index: 30;
  top: 52px;
  left: 0;
  right: 0;
  max-height: 260px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e6e8eb;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  padding: 4px;
`;
const ItemBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  font-size: 14px;
  color: #222;
  background: ${({ active }) => (active ? "#eef3fb" : "transparent")};
  border: none;
  border-radius: 8px;
  cursor: pointer;
`;
const Cat = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: #888;
  background: #f0f2f5;
  border-radius: 6px;
  padding: 2px 8px;
`;
const Nm = styled.span`
  word-break: break-word;
`;

export default ProductAutocomplete;
